import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { APP_ID } from '@funderpro/shared';
import { getToken, API_BASE } from '../../lib/auth';

export function DepositPage() {
  const nav = useNavigate();
  const [amount, setAmount] = useState(100);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const t = getToken();
      const res = await fetch(`${API_BASE}/api/apps/${APP_ID}/payments/deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
        body: JSON.stringify({ amount }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Deposit failed');
      if (data.checkout_url) {
        // Stripe Checkout: redirect
        window.location.href = data.checkout_url;
      } else {
        alert('Deposit created: ' + (data.message || JSON.stringify(data)));
        nav('/portal/wallet', { replace: true });
      }
    } catch (e: any) {
      setErr(e?.message || 'Deposit failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-xl space-y-6" data-testid="deposit-page">
      <Link to="/portal/wallet" className="text-sm text-[#7e9aff]">← Back to wallet</Link>
      <h1 className="text-2xl font-extrabold">Deposit funds</h1>
      <p className="text-sm text-[#9cabd6]">Secure payments via Stripe. Funds are credited once payment clears.</p>
      <form onSubmit={onSubmit} className="rounded-3xl border border-[#1f2640]/60 bg-[#0b101d] p-6 space-y-4">
        {err && <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{err}</div>}
        <div>
          <label className="block text-sm font-semibold text-[#9cabd6]">Amount (USD)</label>
          <input type="number" min={1} step={1} value={amount} onChange={e => setAmount(Number(e.target.value))} required
            className="mt-1 w-full rounded-2xl border border-[#1f2640]/60 bg-[#080c16] p-3 text-white focus:border-[#345ffb] focus:outline-none" />
        </div>
        <button type="submit" disabled={busy} className="w-full rounded-2xl bg-[#345ffb] px-6 py-3 text-base font-bold text-white hover:bg-[#6989fc] disabled:opacity-50">
          {busy ? 'Redirecting…' : `Deposit $${amount}`}
        </button>
        <p className="text-xs text-[#9cabd6]">By depositing, you agree to the <Link to="/terms-conditions" className="text-[#7e9aff]">terms</Link>.</p>
      </form>
    </div>
  );
}

export function WithdrawPage() {
  const nav = useNavigate();
  const [amount, setAmount] = useState(50);
  const [method, setMethod] = useState('bank');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const t = getToken();
      const res = await fetch(`${API_BASE}/api/apps/${APP_ID}/payments/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
        body: JSON.stringify({ amount, method }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Withdrawal failed');
      alert(`Withdrawal request created (id ${data.id || '?'}). Funds will arrive in 1-3 business days.`);
      nav('/portal/wallet', { replace: true });
    } catch (e: any) {
      setErr(e?.message || 'Withdrawal failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-xl space-y-6" data-testid="withdraw-page">
      <Link to="/portal/wallet" className="text-sm text-[#7e9aff]">← Back to wallet</Link>
      <h1 className="text-2xl font-extrabold">Withdraw funds</h1>
      <p className="text-sm text-[#9cabd6]">Bank transfer, USDT (TRC-20), or wire. Settles in 1-3 business days.</p>
      <form onSubmit={onSubmit} className="rounded-3xl border border-[#1f2640]/60 bg-[#0b101d] p-6 space-y-4">
        {err && <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{err}</div>}
        <div>
          <label className="block text-sm font-semibold text-[#9cabd6]">Amount (USD)</label>
          <input type="number" min={1} step={1} value={amount} onChange={e => setAmount(Number(e.target.value))} required
            className="mt-1 w-full rounded-2xl border border-[#1f2640]/60 bg-[#080c16] p-3 text-white focus:border-[#345ffb] focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#9cabd6]">Method</label>
          <select value={method} onChange={e => setMethod(e.target.value)}
            className="mt-1 w-full rounded-2xl border border-[#1f2640]/60 bg-[#080c16] p-3 text-white focus:border-[#345ffb] focus:outline-none">
            <option value="bank">Bank transfer (ACH)</option>
            <option value="wire">Wire transfer</option>
            <option value="usdt">USDT (TRC-20)</option>
          </select>
        </div>
        <button type="submit" disabled={busy} className="w-full rounded-2xl bg-[#345ffb] px-6 py-3 text-base font-bold text-white hover:bg-[#6989fc] disabled:opacity-50">
          {busy ? 'Submitting…' : `Request $${amount} withdrawal`}
        </button>
      </form>
    </div>
  );
}
